import Component from '@glimmer/component';
import { tracked, cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import { assert } from '@ember/debug';
import { isNone } from '@ember/utils';
import { computeGeometry, tickPositions } from '../utils/graphUtils.ts';
import type Owner from '@ember/owner';
import type {
  GraphCoordinate,
  GraphOptions,
  GraphModel,
  GraphGeometry,
  CircleCoordinate,
  HoverTextValues,
  HoverTitles,
} from '../index.d.ts';
import { getClosestDataX, getXValues, getYValues, getCircleCoordinates } from '../utils/hoverUtils.ts';
import envelopeFill from '../helpers/envelope-fill.ts';
import envelopeUpper from '../helpers/envelope-upper.ts';
import envelopeLower from '../helpers/envelope-lower.ts';
import functionLine from '../helpers/function-line.ts';
import onMoveOutside from '../modifiers/on-move-outside.ts';

interface AditoolsGraphArgs {
  model: GraphModel;
  options: GraphOptions;
  xTitleKey: string;
  yTitleKey: string;
  width?: number;
  height?: number;
  leftMargin?: number;
  topMargin?: number;
  rightMargin?: number;
  bottomMargin?: number;
  leftLabelOffset?: number;
  bottomLabelOffset?: number;
  lineClass?: string;
  onHighlightedLinesChanged?: (highlightedLines: number[]) => void;
}

interface AditoolsGraphSignature {
  Args: AditoolsGraphArgs;
  Element: SVGSVGElement;
  Blocks: { default: [GraphGeometry] };
}

export default class AditoolsGraph extends Component<AditoolsGraphSignature> {
  envelopeFill = envelopeFill;
  envelopeUpper = envelopeUpper;
  envelopeLower = envelopeLower;
  functionLine = functionLine;
  onMoveOutside = onMoveOutside;

  @tracked hoverReady = false;
  @tracked hoverClientX = 0;
  @tracked hoverClientY = 0;
  hoverBox: [number, number] = [80, 80];
  @tracked prevHighlightedLines: number[] = [];

  // On initialization, verify required parameters have usable structure.

  constructor(owner: Owner, args: AditoolsGraphArgs) {
    super(owner, args);

    assert('`model` is required', !isNone(this.args.model));
    assert('`options` is required', !isNone(this.args.options));
    assert('`xTitleKey` is required', !isNone(this.args.xTitleKey));
    assert('`yTitleKey` is required', !isNone(this.args.yTitleKey));
    assert('`model` requires a limits member', !isNone(this.args.model) && !isNone(this.args.model.limits));

    if (!isNone(this.args.model) && !isNone(this.args.model.limits)) {
      const limits = this.args.model.limits;
      assert(`model.limits.xmin contains non-numerical value ${limits.xmin}`, !isNaN(limits.xmin));
      assert(`model.limits.xmax contains non-numerical value ${limits.xmax}`, !isNaN(limits.xmax));
      assert(`model.limits.ymin contains non-numerical value ${limits.ymin}`, !isNaN(limits.ymin));
      assert(`model.limits.ymax contains non-numerical value ${limits.ymax}`, !isNaN(limits.ymax));
    }
  }

  // Optional parameters with defaults applied
  get leftMargin(): number {
    return this.args.leftMargin ?? 70;
  }
  get rightMargin(): number {
    return this.args.rightMargin ?? 5;
  }
  get topMargin(): number {
    return this.args.topMargin ?? 15;
  }
  get bottomMargin(): number {
    return this.args.bottomMargin ?? 65;
  }
  get leftLabelOffset(): number {
    return this.args.leftLabelOffset ?? 55;
  }
  get bottomLabelOffset(): number {
    return this.args.bottomLabelOffset ?? 55;
  }
  get width(): number {
    const _default = 672;
    const _value = typeof this.args.width === 'undefined' ? _default : this.args.width;
    return _value;
  }
  get height(): number {
    const _default = 485;
    const _value = typeof this.args.height === 'undefined' ? _default : this.args.height;
    return _value;
  }

  get chartWidth(): number {
    let value = this.width - (this.leftMargin + this.rightMargin + (this.args.options.hover ? 25 : 0));
    if (value < 0) value = 0;
    return value;
  }

  get chartHeight(): number {
    let value = this.height - (this.topMargin + this.bottomMargin);
    if (value < 0) {
      value = 0;
    }
    return value;
  }

  get xGridLineEnd(): number {
    return this.args.options.xticksline === undefined
      ? 0
      : this.args.options.xticksline == 0
        ? this.chartHeight
        : <number>this.args.options.xticksline > this.chartHeight
          ? 0
          : this.chartHeight - <number>this.args.options.xticksline;
  }

  get yGridLineEnd(): number {
    return this.args.options.yticksline === undefined
      ? this.chartWidth
      : this.args.options.yticksline == 0
        ? 0
        : <number>this.args.options.yticksline > this.chartWidth
          ? this.chartWidth
          : this.args.options.yticksline;
  }

  get lineClasses(): string[] {
    if (this.args.lineClass) {
      return [this.args.lineClass];
    } else {
      return ['default-line-class'];
    }
  }

  get geometry(): GraphGeometry {
    return computeGeometry(this.args.model.limits, this.args.options, this.chartWidth, this.chartHeight);
  }

  get xTitleCenter(): GraphCoordinate {
    return {
      x: this.chartWidth / 2,
      y: this.chartHeight + this.bottomLabelOffset,
    };
  }

  get yTitleCenter(): GraphCoordinate {
    return { x: -this.leftLabelOffset, y: this.chartHeight / 2 };
  }

  get xTicks(): { value: string; x: number; y: number }[] {
    const count = this.geometry.tickcount[0] instanceof Array ? 5 : this.geometry.tickcount[0];
    return tickPositions(
      this.args.model.limits ? this.args.model.limits.xmin : 0,
      this.args.model.limits ? this.args.model.limits.xmax : 0,
      this.args.options.xticks || 5,
      this.args.options.xscale,
      this.args.options.xformat,
      this.geometry.xScale,
      count,
    ).map((item) => {
      return { value: item.value, x: item.coordinate, y: this.geometry.height };
    });
  }

  get yTicks(): { value: string; x: number; y: number }[] {
    const count = this.geometry.tickcount[1] instanceof Array ? 5 : this.geometry.tickcount[1];
    return tickPositions(
      this.args.model.limits ? this.args.model.limits.ymin : 0,
      this.args.model.limits ? this.args.model.limits.ymax : 0,
      this.args.options.yticks || 5,
      this.args.options.yscale,
      this.args.options.yformat,
      this.geometry.yScale,
      count,
    ).map((item) => {
      return { value: item.value, x: 0, y: item.coordinate };
    });
  }

  get hoverTitles(): HoverTitles {
    if (this.args.options.hoverTitles) {
      return this.args.options.hoverTitles;
    } else {
      return { headerLabel: '', lineLabels: [''] };
    }
  }

  get hoverPosition(): { x: number; y: number } {
    return {
      x: Math.max(0, this.hoverClientX - this.hoverBox[0] / 2) - 30,
      y: Math.max(0, this.hoverClientY - this.hoverBox[1]),
    };
  }

  @cached
  get closestXValue(): number {
    return getClosestDataX(this.hoverClientX - this.leftMargin, [this.args.model.data], this.geometry);
  }

  get circleCoordinates(): CircleCoordinate[] {
    return getCircleCoordinates(this.closestXValue, this.args.options, this.geometry, [this.args.model.data]);
  }

  get hoverText(): HoverTextValues {
    const { formattedValue, formattedValueWithUnit } = getXValues(this.closestXValue, this.args.options);
    const yValues = getYValues(
      this.closestXValue,
      this.args.options,
      [this.geometry.xScale(this.closestXValue), this.hoverClientY - this.topMargin],
      [this.args.model.data],
      this.geometry,
    );
    return {
      xAxisValueWithUnit: formattedValueWithUnit,
      xAxisValue: formattedValue,
      yAxisValues: yValues,
    };
  }

  get highlightedLines(): number[] {
    if (this.hoverText.yAxisValues.find((y) => y.focused == true)) {
      const indexLines: number[] = this.hoverText.yAxisValues
        .filter((y) => y.focused == true)
        .map((y) => y.index)
        .sort(function (a, b) {
          return a - b;
        });
      return indexLines;
    }
    return [];
  }

  @action updateHover(event: MouseEvent): void {
    if (this.args.options.hover) {
      const target = <HTMLInputElement>event.target;
      if (event.target && target.parentElement) {
        this.hoverClientX = event.offsetX;
        this.hoverClientY = event.offsetY;
        this.hoverReady = true;

        if (this.args.onHighlightedLinesChanged) {
          const a = performance.now();
          if (this.prevHighlightedLines.length != this.highlightedLines.length) {
            this.args.onHighlightedLinesChanged(this.highlightedLines);
            this.prevHighlightedLines = [...this.highlightedLines];

            const b = performance.now();
            if (b - a > 20) {
              console.log(`Long execution time: It took ${b - a} ms to highlight line changes`);
            }
          } else {
            const containsAll = this.prevHighlightedLines.every((element) => {
              return this.highlightedLines?.indexOf(element) !== -1;
            });
            if (!containsAll) {
              this.args.onHighlightedLinesChanged(this.highlightedLines);
              this.prevHighlightedLines = [...this.highlightedLines];

              const b = performance.now();
              if (b - a > 20) {
                console.log(`Long execution time: It took ${b - a} ms to highlight line changes`);
              }
            }
          }
        }
      }
    }
  }

  @action endHover(target: Element): void {
    let isSummary = false;
    for (let i = 0; i < target.classList.length; i++) {
      if (target.classList[i] === 'graph-hover_summary-line') {
        isSummary = true;
        break;
      }
    }
    if (this.hoverReady && !isSummary) {
      this.hoverReady = false;
    }
  }

  get showHover(): boolean {
    return !!this.hoverReady;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    AditoolsGraph: typeof AditoolsGraph;
  }
}
